import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import BEMHelper from '@ynap/bem-helper';
import Overlay from '@ynap/overlay';
import ViewportObserver from '@ynap/viewport-observer';
import ProductInformationWithSizeAndFit from '../ProductInformationWithSizeAndFit/ProductInformationWithSizeAndFit';
import { useContentByComponentKey } from '@ynap/content-hook';
import HowMeasurementsAreMade from '../HowMeasurementsAreMade/HowMeasurementsAreMade';
import normalizeHMAMData from './normalizeHMAMData';
import fetchMatchingHmamContentByMasterCategories from './fetchMatchingHmamContentByMasterCategories';
import { DeliveryAndReturnsSummary } from '../DeliveryAndReturns';
import { Features } from '@ynap/feature-selector';
import { pushData as pushDataToLivePerson } from '@ynap/live-chat';
import SizeConversionsTable from '../SizeConversionsTable/SizeConversionsTable';
import track from 'react-tracking';
import { withAnalytics } from '@ynap/analytics';
import { trackSizeGuideComplete } from '../analytics';
import ProductMeasurements from './ProductMeasurements';
import { useAppState, useConfig } from '@ynap/immutables-provider';

const bem = new BEMHelper('SizeChart');

export const SizeChartFeatureWrapper = (props) => (
    <Features>
        <SizeChart {...props} />
    </Features>
);
const SizeChart = ({
    features,
    sizeAndFitInformation,
    sizeMeasurement,
    sizeConversion,
    sizeConversionBySku,
    measurementType,
    messages,
    name = '',
    designerName = '',
    image = {},
    closeModal,
    skus,
    masterCategory,
    changeSelectedSku = () => {},
    selectedSku = null,
    safeTrackEvent,
    sizeScheme,
    isEip,
}) => {
    const config = useConfig();
    const { language } = useAppState();
    const { sizes, deliveryAndReturns } = messages;
    const { sizeGuide } = sizes;
    const [units, setUnits] = useState('cm');
    const enableSizeGuideLiveChat = config?.head?.pdp?.enableSizeGuideLiveChat;
    const liveChatAvailableLanguageList = config?.head?.pdp?.liveChatAvailableLanguageList || [];

    // live chat handler
    // it adds the additional section "size guide" on open and removes it on close
    useEffect(() => {
        // atm the livechat is only enabled for NAP on EN language
        if (enableSizeGuideLiveChat && liveChatAvailableLanguageList.includes(language?.toLowerCase())) {
            const parameters = {
                sectionData: window?.lpTag?.section || [],
                pageSdes: window?.lpTag?.sdes?.get?.()?.prodView?.[0] || {},
                additionalSections: ['size guide'],
            };
            pushDataToLivePerson(parameters);

            return () => {
                const sectionData = [...(window?.lpTag?.section || [])];
                const sizeGuidePosition = sectionData.indexOf('size guide');

                if (sizeGuidePosition > -1) {
                    sectionData.splice(sizeGuidePosition, 1);
                }

                const parameters = {
                    sectionData,
                    pageSdes: window?.lpTag?.sdes?.get?.()?.prodView?.[0] || {},
                };
                pushDataToLivePerson(parameters);
            };
        }
    }, []);

    const toggleUnits = () => setUnits(units === 'cm' ? 'in' : 'cm');

    const { showHMAM: { withOverlay, coreMediaKey, componentKey, sizeGuideTitle } = {} } = features;
    const showSizeAndFit = !features?.hideSizeAndFit;
    const onSelectSku = (header) => {
        const sizeSku = skus.find((sku) => sku.size.centralSizeLabel === header || sku.size.labelSize === header);
        changeSelectedSku(sizeSku, sizeSku.size.labelSize);
    };

    const { content: hmamContent, loading: loadingHMAM } = useContentByComponentKey({
        componentKey,
        normalize: normalizeHMAMData,
    });

    const [showMeasuringGuide, setMeasuringGuide] = useState('');
    const [measurements, setMeasurements] = useState([]);
    const goBack = () => {
        setMeasurements([]);
        setMeasuringGuide('');
    };

    const getHMAM = (showMeasuringGuide, hmamContent, withOverlay, coreMediaKey) => {
        if ((showMeasuringGuide || !withOverlay) && Object.entries(hmamContent || []).length) {
            const picture = fetchMatchingHmamContentByMasterCategories(showMeasuringGuide, masterCategory, hmamContent, withOverlay, coreMediaKey);
            // if we are not returned a picture at least the HowMeasurementsAreMade will not display.
            if (picture) {
                !showMeasuringGuide &&
                    !withOverlay &&
                    measurements.length < 3 &&
                    Object.keys(hmamContent).map((key) => {
                        measurements.push({ ...hmamContent[key], measurementLabel: key });
                    });
                if (!withOverlay) {
                    return (
                        <HowMeasurementsAreMade
                            picture={picture}
                            setMeasuringGuide={setMeasuringGuide}
                            measurements={measurements}
                            modifiers={{ withoutOverlay: !withOverlay }}
                        />
                    );
                } else {
                    return (
                        <Overlay
                            className={bem('sizeGuide')}
                            onClose={closeModal}
                            handleBodyClick={closeModal}
                            title={sizeGuide.measuringGuide()}
                            goBack={goBack}
                        >
                            <HowMeasurementsAreMade picture={picture} setMeasuringGuide={setMeasuringGuide} measurements={measurements} />
                        </Overlay>
                    );
                }
            } else {
                // we go back if there is no category, but we shouldn't get here with the new logic
                setMeasurements([]);
                setMeasuringGuide('');
            }
        }
    };

    if (showMeasuringGuide && hmamContent && withOverlay) {
        return getHMAM(showMeasuringGuide, hmamContent, withOverlay, coreMediaKey);
    }
    const sizeMeasurementData = sizeMeasurement && sizeMeasurement[units];
    const sizeGuideTitleFeature = sizeGuideTitle?.()?.props?.children;
    const hideSizeConversionMessage =
        config?.head?.pdp?.hideSizeConversionMessage?.includes(designerName) && config?.head?.pdp?.category?.includes(masterCategory[1].label);

    const output = (withOverlay || !loadingHMAM) && (
        <Overlay className={bem('sizeGuide')} onClose={closeModal} handleBodyClick={closeModal} title={sizeGuideTitleFeature}>
            <ProductInformationWithSizeAndFit
                name={name}
                designerName={designerName}
                sizeAndFitInformation={showSizeAndFit && sizeAndFitInformation}
                image={image}
                messages={messages}
            />
            {withOverlay ? (
                <div data-testid="size-chart-tables" className={bem('tables')}>
                    <ProductMeasurements
                        {...{
                            features,
                            sizeMeasurementData,
                            sizeGuide,
                            measurementType,
                            setMeasurements,
                            toggleUnits,
                            changeSelectedSku,
                            onSelectSku,
                            masterCategory,
                            skus,
                            selectedSku,
                            setMeasuringGuide,
                            hmamContent,
                            loadingHMAM,
                            bem,
                        }}
                    />
                    {!hideSizeConversionMessage && (
                        <SizeConversionsTable
                            features={features}
                            sizeData={sizeConversion}
                            conversions={sizeConversionBySku}
                            title={sizeGuide.sizeConversionsTitle}
                            details={sizeGuide.sizeConversionsDetails}
                            slideToScroll={sizeGuide.slideToScroll}
                            sizeWrapperClass={bem('sizeConversionWrapper')}
                            onSelectSku={changeSelectedSku ? onSelectSku : null}
                            skus={skus}
                            sizeScheme={sizeScheme}
                            selectedSku={selectedSku}
                            designerName={designerName}
                            masterCategory={masterCategory}
                        />
                    )}
                </div>
            ) : (
                getHMAM(showMeasuringGuide, hmamContent, withOverlay, coreMediaKey)
            )}
            <ViewportObserver
                threshold={0.75}
                onIntersect={() => {
                    safeTrackEvent(trackSizeGuideComplete);
                }}
            >
                {() => (
                    <div className={bem('footer')}>
                        <div className={bem('returnsAndContactWrapper')}>
                            <DeliveryAndReturnsSummary className={bem('deliveryAndReturns')} messages={{ deliveryAndReturns }} isEip={isEip} />
                        </div>
                    </div>
                )}
            </ViewportObserver>
        </Overlay>
    );
    return output;
};
const BasePropTypes = {
    sizeConversion: PropTypes.shape({ headers: PropTypes.array, rows: PropTypes.array }).isRequired,
    sizeConversionBySku: PropTypes.array,
    sizeMeasurement: PropTypes.shape({ headers: PropTypes.array, rows: PropTypes.array }),
    messages: PropTypes.object,
    className: PropTypes.object,
    onOpen: PropTypes.func,
    closeModal: PropTypes.func,
    name: PropTypes.string.isRequired,
    designerName: PropTypes.string.isRequired,
    sizeAndFitInformation: PropTypes.element,
    image: PropTypes.object,
    skus: PropTypes.array,
    changeSelectedSku: PropTypes.func,
    selectedSku: PropTypes.string,
    setMeasuringGuide: PropTypes.func,
    setMeasurements: PropTypes.func,
    masterCategory: PropTypes.array,
};

const PropTypesWithFeatures = {
    features: PropTypes.object,
    ...BasePropTypes,
};

SizeChartFeatureWrapper.propTypes = BasePropTypes;
SizeChart.propTypes = PropTypesWithFeatures;

export default withAnalytics(track()(SizeChartFeatureWrapper));
